// TreeSelect.tsx
// React 18 + TypeScript + MUI v5 (@mui/material + @mui/lab)

import * as React from "react";
import { ReactNode, useMemo, useState } from "react";
import { TextField, Box, Typography } from "@mui/material";
import TreeView from "@mui/lab/TreeView";
import TreeItem, { treeItemClasses } from "@mui/lab/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { styled, useTheme } from "@mui/material/styles";
import {
  CheckCircleIcon,
  DownArrowIcon,
  KbFileIcon,
  KbFolderDualIcon,
} from "components/icons";
import { Input } from "components/input";

export type TreeNode<T = unknown> = {
  id: string;
  label: ReactNode;
  type: string;
  children?: TreeNode<T>[];
  disabled?: boolean;
  data?: T;
};

export type TreeSelectProps<T = unknown> = {
  nodes: TreeNode<T>[];

  /** Controlled selection ids */
  value: string[];

  /** Called with next value */
  onChange: (nextValue: string[], toggledId: string) => void;

  /** Allow multiple selection; single behaves like radio */
  multiple?: boolean;

  /** on tree item click */
  onNodeClick?: (node: TreeNode<T>) => void;

  /** Optional search box */
  enableSearch?: boolean;
  searchPlaceholder?: string;
  searchText?: string;
  onSearchTextChange?: (s: string) => void;

  /** Provide your own stringifier for labels (for search) */
  getLabelText?: (n: TreeNode<T>) => string;

  /** Filter function (overrides built-in search) */
  filterNode?: (n: TreeNode<T>, ancestors: TreeNode<T>[]) => boolean;

  /** Custom label renderer */
  renderLabel?: (n: TreeNode<T>) => ReactNode;

  /** Disable logic override */
  isNodeDisabled?: (n: TreeNode<T>) => boolean;

  /** MUI slots */
  slots?: {
    ExpandIcon?: React.ElementType;
    CollapseIcon?: React.ElementType;
    EndIcon?: React.ElementType;
    NoResults?: React.ElementType;
  };

  /** Style hook */
  sx?: any;

  /** Controlled expanded nodes (optional); if omitted we manage them internally */
  expanded?: string[];
  onExpandedChange?: (ids: string[]) => void;

  /** Show child count in folder labels */
  showChildCount?: boolean;

  /** Show check icon on selected items (default: true) */
  showSelectedCheckIcon?: boolean;

  /** Custom sx for selected tree item content */
  selectedSx?: Record<string, unknown>;
};

const StyledTreeItem = styled(TreeItem)(() => ({
  [`& .${treeItemClasses.content}`]: {
    padding: "4px 8px",
    borderRadius: "4px",
    marginBottom: "8px",
    cursor: "pointer",
    "& .folder-icon": {
      display: "block",
    },
    "& .down-icon": {
      display: "none",
    },
    "&:hover": {
      "& .folder-icon": {
        display: "none",
      },
      "& .down-icon": {
        display: "block",
      },
    },
  },
  [`& .${treeItemClasses.label}`]: {},
  [`& .${treeItemClasses.iconContainer}`]: {
    width: "24px",
    height: "24px",
  },
}));

function defaultGetLabelText<T>(n: TreeNode<T>) {
  if (typeof n.label === "string") return n.label;
  return "";
}

function filterTree<T>(
  nodes: TreeNode<T>[],
  isMatch: (n: TreeNode<T>, ancestors: TreeNode<T>[]) => boolean,
): TreeNode<T>[] {
  const walk = (
    list: TreeNode<T>[],
    ancestors: TreeNode<T>[],
  ): TreeNode<T>[] => {
    const out: TreeNode<T>[] = [];
    for (const n of list) {
      const kids = n.children ? walk(n.children, [...ancestors, n]) : [];
      const match = isMatch(n, ancestors);
      if (match || kids.length) {
        out.push({ ...n, children: kids });
      }
    }
    return out;
  };
  return walk(nodes, []);
}

export const RichTreeView = ({
  nodes,
  value,
  onChange,
  multiple = true,
  onNodeClick,
  enableSearch = false,
  searchPlaceholder = "Search…",
  searchText: searchTextProp,
  onSearchTextChange,
  getLabelText = defaultGetLabelText,
  filterNode,
  renderLabel,
  isNodeDisabled,
  slots,
  sx,
  expanded: expandedProp,
  onExpandedChange,
  showChildCount = false,
  showSelectedCheckIcon = true,
  selectedSx,
}: TreeSelectProps) => {
  const theme = useTheme();
  const [internalExpanded, setInternalExpanded] = useState<string[]>([]);
  const expanded = expandedProp ?? internalExpanded;
  const setExpanded = onExpandedChange ?? setInternalExpanded;

  const [internalSearch, setInternalSearch] = useState("");
  const searchText = searchTextProp ?? internalSearch;

  const ExpandIcon = slots?.ExpandIcon ?? ChevronRightIcon;
  const CollapseIcon = slots?.CollapseIcon ?? ExpandMoreIcon;

  const effectiveFilter =
    filterNode ??
    ((n: TreeNode) =>
      !searchText
        ? true
        : getLabelText(n).toLowerCase().includes(searchText.toLowerCase()));

  const filteredNodes = useMemo(
    () => filterTree(nodes, (n, ancs) => effectiveFilter(n, ancs)),
    [nodes, searchText, effectiveFilter],
  );

  const handleSelect = (id: string) => {
    let next: string[] = [];
    if (multiple) {
      const exists = value.includes(id);
      next = exists ? value.filter((v) => v !== id) : [...value, id];
    } else {
      next = value.includes(id) ? [] : [id];
    }
    onChange(next, id);
  };

  const renderTree = (list: TreeNode[]) =>
    list.map((n) => {
      const kids = n.children ?? [];
      const selected = value.includes(n.id);
      const disabled = isNodeDisabled?.(n) ?? !!n.disabled;
      return (
        <StyledTreeItem
          sx={{
            "&.MuiTreeItem-root": {
              "& > .MuiTreeItem-content": {
                ...(selected
                  ? (selectedSx ?? {
                      backgroundColor: theme.palette.primitive.brand.purple[25],
                    })
                  : {}),
                paddingX: "8px",
              },
              "& .MuiTreeItem-iconContainer": {
                width: "24px",
                height: "24px",
              },
            },
          }}
          ContentProps={{
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              onNodeClick?.(n);
            },
          }}
          icon={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "24px",
                height: "24px",
              }}
            >
              {n?.type === "folder" ? (
                <KbFolderDualIcon
                  sx={{ width: "24px", height: "24px" }}
                  className={`${kids.length > 0 ? "folder-icon" : ""}`}
                  color="primary"
                />
              ) : (
                <KbFileIcon color="primary" />
              )}
              {kids.length > 0 && (
                <DownArrowIcon className="down-icon" color="primary" />
              )}
            </Box>
          }
          key={n.id}
          nodeId={n.id}
          label={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                ...theme.typography["body-small"],
              }}
              onClick={(e) => {
                e.stopPropagation();
                onNodeClick?.(n);
                if (!disabled) handleSelect(n.id);
              }}
            >
              {renderLabel ? renderLabel(n) : n.label}{" "}
              {showChildCount && kids.length > 0 && (
                <Typography
                  variant="body-small"
                  color={theme.palette.lightBg.low}
                >
                  {kids.length}
                </Typography>
              )}
              {selected && showSelectedCheckIcon && (
                <CheckCircleIcon
                  fontSize="small"
                  htmlColor={theme.palette.primitive.brand.purple[700]}
                />
              )}
            </Box>
          }
          disabled={disabled}
        >
          {kids.length ? renderTree(kids) : null}
        </StyledTreeItem>
      );
    });

  const onNodeToggle = (_: React.SyntheticEvent, ids: string[]) =>
    setExpanded(ids);

  return (
    <Box sx={sx}>
      {enableSearch && (
        <Box sx={{ mb: 1 }}>
          <Input
            name="search"
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(e) =>
              onSearchTextChange
                ? onSearchTextChange(e.target.value)
                : setInternalSearch(e.target.value)
            }
          />
        </Box>
      )}

      {filteredNodes.length === 0 ? (
        slots?.NoResults ? (
          React.createElement(slots.NoResults)
        ) : (
          <Typography
            variant="body-medium"
            color={theme.palette.lightBg.medium}
          >
            No results
          </Typography>
        )
      ) : (
        <TreeView
          expanded={expanded}
          onNodeToggle={onNodeToggle}
          defaultCollapseIcon={<CollapseIcon />}
          defaultExpandIcon={<ExpandIcon />}
          disableSelection
          multiSelect={false}
        >
          {renderTree(filteredNodes)}
        </TreeView>
      )}
    </Box>
  );
};
